import { z } from 'zod';
import { CapabilityReportSchema, ProfileSchema } from './profiles.js';
/** Versão do texto de transparência/consentimento exibido no app antes do pareamento. */
export const CONSENT_VERSION = '2026-11-p1-v1';
/** Intervalo de heartbeat sugerido ao agente (o WorkManager impõe mínimo de 15 min). */
export const HEARTBEAT_INTERVAL_SECONDS = 15 * 60;
/** Perfis que podem ser solicitados por código de pareamento. O corporativo exige provisionamento oficial. */
export const PairableProfileSchema = z.enum(['MONITORING', 'ASSISTED']);
/** Código de pareamento: 8 caracteres base32 Crockford, exibido como XXXX-XXXX. */
export const ENROLLMENT_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export function normalizeEnrollmentCode(input) {
    const s = input.toUpperCase().replace(/[\s-]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');
    if (s.length !== 8 || [...s].some((c) => !ENROLLMENT_CODE_ALPHABET.includes(c)))
        return null;
    return s;
}
export function formatEnrollmentCode(normalized) {
    return `${normalized.slice(0, 4)}-${normalized.slice(4)}`;
}
/** Informações do aparelho. Nada de conteúdo pessoal: sem apps, mensagens, contatos ou navegação. */
export const DeviceInfoSchema = z.object({
    manufacturer: z.string().max(64),
    brand: z.string().max(64).optional(),
    model: z.string().max(64),
    androidRelease: z.string().max(16),
    sdkInt: z.number().int().min(28).max(100),
    securityPatch: z.string().max(16).optional(),
    agentVersionName: z.string().max(32),
    agentVersionCode: z.number().int().min(1),
});
/** Estado das permissões/configurações relevantes ao agente. */
export const PermissionReportSchema = z.object({
    /** POST_NOTIFICATIONS (Android 13+). */
    notifications: z.enum(['granted', 'denied', 'not_required']),
    /** Isento de otimização de bateria (melhora a regularidade do heartbeat). */
    batteryOptimizationIgnored: z.boolean(),
    /** Localização não é coletada nesta versão. */
    location: z.literal('not_collected'),
});
export const TelemetrySchema = z.object({
    batteryPercent: z.number().int().min(0).max(100).optional(),
    charging: z.boolean().optional(),
    storageFreeBytes: z.number().int().nonnegative().optional(),
    storageTotalBytes: z.number().int().nonnegative().optional(),
    network: z.enum(['wifi', 'cellular', 'ethernet', 'other', 'none']).optional(),
});
export const EnrollmentPreviewRequestSchema = z.object({ code: z.string().min(8).max(16) });
export const EnrollRequestSchema = z.object({
    code: z.string().min(8).max(16),
    /** Chave pública EC P-256 em SubjectPublicKeyInfo DER, base64. A privada nunca sai do Android Keystore. */
    publicKey: z.string().min(80).max(400),
    keyAlgorithm: z.literal('EC_P256_SHA256'),
    device: DeviceInfoSchema,
    capabilities: CapabilityReportSchema,
    permissions: PermissionReportSchema,
    consentVersion: z.string().max(32),
});
export const HeartbeatRequestSchema = z.object({
    device: DeviceInfoSchema,
    capabilities: CapabilityReportSchema,
    permissions: PermissionReportSchema,
    telemetry: TelemetrySchema,
});
export const CommandTypeSchema = z.enum(['PING', 'REFRESH_INVENTORY']);
export const CommandStatusSchema = z.enum([
    'QUEUED',
    'DELIVERED',
    'SUCCEEDED',
    'FAILED',
    'EXPIRED',
    'CANCELED',
]);
export const CommandResultRequestSchema = z.object({
    status: z.enum(['SUCCEEDED', 'FAILED']),
    error: z.string().max(500).optional(),
    result: z.record(z.string(), z.unknown()).optional(),
});
export { ProfileSchema };
/**
 * Texto assinado pelo agente em cada requisição autenticada (ECDSA P-256 / SHA-256, assinatura DER):
 *   METHOD \n PATH(+query) \n TIMESTAMP_MS \n NONCE \n BASE64URL(SHA256(corpo bruto))
 * O mesmo formato é implementado no app (DeviceSigner.kt) e coberto por vetor de teste comum
 * (packages/shared/fixtures/device-signature.json).
 */
export function deviceSigningString(method, pathWithQuery, timestampMs, nonce, bodySha256B64Url) {
    return [method.toUpperCase(), pathWithQuery, timestampMs, nonce, bodySha256B64Url].join('\n');
}
export const DEVICE_HEADERS = {
    id: 'x-ec-device',
    timestamp: 'x-ec-timestamp',
    nonce: 'x-ec-nonce',
    signature: 'x-ec-signature',
};
/** Janela aceita entre o relógio do aparelho e o do servidor. */
export const DEVICE_CLOCK_SKEW_MS = 5 * 60 * 1000;
//# sourceMappingURL=device.js.map